import { useEffect, useState } from 'react';
import { useRoomStore } from '@features/room';
import { getBackendApiClient } from '@features/backend-api';
import type { BackendMeeting, MeetingStatus } from '@shared/types';
import {
  Panel,
  ActionButton,
  JoinIcon,
  CalendarIcon,
  BroadcastIcon,
  LoadingIcon,
  CloseIcon,
} from '@shared/ui';

interface RoomsListPanelProps {
  onJoinRoom: (meeting: BackendMeeting) => void | Promise<void>;
  /** Активная комната (подсветить в списке), если участник уже подключён. */
  activeMeetingId?: string | null;
}

const STATUS_LABEL: Record<MeetingStatus, string> = {
  Scheduled: 'Scheduled',
  Live: 'Live',
  Ended: 'Ended',
  Cancelled: 'Cancelled',
};

const STATUS_TONE: Record<MeetingStatus, string> = {
  Scheduled: 'text-text-secondary',
  Live: 'text-status-live',
  Ended: 'text-text-muted',
  Cancelled: 'text-text-muted',
};

const CLOSED_STATUSES: MeetingStatus[] = ['Ended', 'Cancelled'];

export function RoomsListPanel({ onJoinRoom, activeMeetingId }: RoomsListPanelProps) {
  const [meetings, setMeetings] = useState<BackendMeeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  const roomError = useRoomStore((s) => s.error);
  const leaveRoom = useRoomStore((s) => s.leave);

  const loadMeetings = () => {
    setIsLoading(true);
    setError(null);
    return getBackendApiClient()
      .listMeetings()
      .then(setMeetings)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load rooms'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    void loadMeetings().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoin = async (meeting: BackendMeeting) => {
    if (joiningId) return;
    setJoiningId(meeting.id);
    try {
      await onJoinRoom(meeting);
    } finally {
      setJoiningId(null);
    }
  };

  const handleClose = async (meeting: BackendMeeting) => {
    if (closingId) return;
    if (!window.confirm(`Close room "${meeting.title}"? This will end the meeting for everyone.`)) return;

    setClosingId(meeting.id);
    try {
      if (meeting.id === activeMeetingId) {
        await leaveRoom();
      }

      if (meeting.status === 'Live') {
        await getBackendApiClient().endMeeting(meeting.id);
      } else if (meeting.status === 'Scheduled') {
        await getBackendApiClient().cancelMeeting(meeting.id);
      }

      await loadMeetings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close room');
    } finally {
      setClosingId(null);
    }
  };

  return (
    <Panel title="ROOMS" titleIcon={<BroadcastIcon className="h-4 w-4 text-accent" />}>
      {roomError && (
        <p className="mb-3 rounded-lg border border-status-live/30 bg-status-live/10 px-3 py-2 text-xs text-status-live">
          {roomError}
        </p>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 py-3 text-sm text-text-muted">
          <LoadingIcon className="h-4 w-4 animate-spin" />
          Loading rooms…
        </div>
      )}

      {!isLoading && error && (
        <p className="rounded-lg border border-status-live/30 bg-status-live/10 px-3 py-2 text-xs text-status-live">
          {error}
        </p>
      )}

      {!isLoading && !error && meetings.length === 0 && (
        <p className="text-sm text-text-muted">No rooms yet</p>
      )}

      {!isLoading && !error && meetings.length > 0 && (
        <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
          {meetings.map((meeting) => (
            <RoomRow
              key={meeting.id}
              meeting={meeting}
              isActive={meeting.id === activeMeetingId}
              isJoining={joiningId === meeting.id}
              isClosing={closingId === meeting.id}
              onJoin={() => handleJoin(meeting)}
              onClose={() => handleClose(meeting)}
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

function RoomRow({
  meeting,
  isActive,
  isJoining,
  isClosing,
  onJoin,
  onClose,
}: {
  meeting: BackendMeeting;
  isActive: boolean;
  isJoining: boolean;
  isClosing: boolean;
  onJoin: () => void;
  onClose: () => void;
}) {
  const canJoin = meeting.status === 'Scheduled' || meeting.status === 'Live';
  const canClose = !CLOSED_STATUSES.includes(meeting.status);

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${isActive ? 'border-accent bg-accent/10' : 'border-surface-border bg-surface-2'
        }`}
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-text-primary">{meeting.title}</div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
          <span className={STATUS_TONE[meeting.status]}>{STATUS_LABEL[meeting.status]}</span>
          <span className="flex items-center gap-1">
            <CalendarIcon className="h-3 w-3" />
            {new Date(meeting.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="flex shrink-0 gap-1.5">
        {canClose && (
          <ActionButton
            variant="danger"
            icon={isClosing ? <LoadingIcon className="h-3.5 w-3.5 animate-spin" /> : <CloseIcon className="h-3.5 w-3.5" />}
            onClick={onClose}
            disabled={isClosing || isJoining}
            className="flex-none w-auto min-h-0 px-2.5 py-1.5 text-xs"
          >
            {isActive ? 'Close room' : isClosing ? 'Closing…' : 'Close'}
          </ActionButton>
        )}

        <ActionButton
          variant={isActive ? 'accent' : 'neutral'}
          icon={isJoining ? <LoadingIcon className="h-3.5 w-3.5 animate-spin" /> : <JoinIcon className="h-3.5 w-3.5" />}
          onClick={onJoin}
          disabled={!canJoin || isActive || isJoining || isClosing}
          className="flex-none w-auto min-h-0 px-2.5 py-1.5 text-xs"
        >
          {isActive ? 'Joined' : isJoining ? 'Joining…' : 'Join'}
        </ActionButton>
      </div>
    </div>
  );
}